import React, { useContext, useState, useMemo, useRef, useEffect } from 'react';
import { Search, Check, MapPin, Users, Clock } from 'lucide-react';
import { ClassesContext, CenterContext, LanguageContext } from '../../../../Context';



/**
 * ClassSelect - Inline class selector for the pipeline test section.
 * Shows Active + Coming classes sorted by test location first, then others.
 * Includes search box, schedule display, and capacity info.
 *
 * @param {Object} props
 * @param {string} props.value - Selected class ID
 * @param {Function} props.onChange - Callback with selected class ID
 * @param {string} [props.testLocation] - Location ID to prioritize classes at
 * @param {string} [props.label] - Label text
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element}
 * @author Forge, opencode glm-5.1
 * @version 1.0
 * @since 2026-04-16
 */
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatSchedule(classDays) {
  if (!classDays?.length) return '';
  return classDays
    .slice()
    .sort((a, b) => a.day - b.day)
    .map((cd) => `${DAY_NAMES[cd.day]} ${cd.startTime || ''}${cd.endTime ? '-' + cd.endTime : ''}`)
    .join(', ');
}

export default function ClassSelect({ value, onChange, testLocation, label, className = '' }) {
  const { classes } = useContext(ClassesContext);
  
  const { getLocation } = useContext(CenterContext);
  const { l, language } = useContext(LanguageContext);
  const [search, setSearch] = useState('');
  const searchRef = useRef(null);

  const t = (key, fallback) => {
    try { return l[key]?.[language] || fallback || key; } catch { return fallback || key; }
  };

  const sortedClasses = useMemo(() => {
    const q = search.toLowerCase().trim();
    const filtered = classes.filter((c) => {
      if (!['Active', 'Coming'].includes(c.status)) return false;
      if (q && !c.name.toLowerCase().includes(q)) return false;
      return true;
    });

    const locationCompare = (a, b) => {
      const aAtLocation = a.location === testLocation;
      const bAtLocation = b.location === testLocation;
      if (aAtLocation && !bAtLocation) return -1;
      if (!aAtLocation && bAtLocation) return 1;
      return a.name.localeCompare(b.name);
    };

    return filtered.sort(locationCompare);
  }, [classes, testLocation, search]);

  useEffect(() => {
    if (searchRef.current) searchRef.current.focus();
  }, []);

  const resolveClassName = (id) => {
    if (!id) return '';
    const cls = classes.find((c) => c._id === id);
    return cls?.name || id;
  };

  const selectedName = resolveClassName(value);

  return (
    <div className={className}>
      {label && (
        <label className="block text-[10px] font-semibold text-orange-700 uppercase tracking-wider mb-1.5">
          {label}
        </label>
      )}

      {value && (
        <div className="flex items-center gap-2 mb-2 px-3 py-2 rounded-[10px] bg-emerald-50 border border-emerald-200">
          <Check size={14} className="text-emerald-600" />
          <span className="text-sm font-semibold text-emerald-800 flex-1">{selectedName}</span>
          <button
            type="button"
            onClick={() => onChange('')}
            className="text-[10px] font-semibold text-emerald-600 hover:text-emerald-800 underline underline-offset-2 cursor-pointer"
          >
            {t('pipeline.clear', 'Clear')}
          </button>
        </div>
      )}

      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          ref={searchRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('pipeline.searchClasses', 'Search classes...')}
          className="w-full pl-8 pr-3 py-2 rounded-[10px] border border-orange-200 text-sm text-slate-800 placeholder:text-slate-300 bg-white focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400/20 transition-colors duration-150"
        />
      </div>

      <div className="mt-2 max-h-[240px] overflow-y-auto rounded-[10px] border border-slate-200 bg-white">
        {sortedClasses.length === 0 ? (
          <div className="py-6 text-center">
            <p className="text-xs text-slate-400 italic">
              {search ? t('pipeline.noMatchingClasses', 'No matching classes') : t('pipeline.noClassesAvailable', 'No classes available')}
            </p>
          </div>
        ) : (
          sortedClasses.map((cls) => {
            const isSelected = value === cls._id;
            const loc = getLocation?.(cls.location);
            const locName = loc?.name || '';
            const isAtTestLocation = cls.location === testLocation;
            const studentCount = cls.students?.length || 0;
            const maxStudents = cls.maxStudents || 0;
            const isFull = maxStudents > 0 && studentCount >= maxStudents;

            return (
              <button
                key={cls._id}
                type="button"
                onClick={() => onChange(cls._id)}
                className={`w-full text-left px-3 py-2.5 border-b border-slate-50 last:border-b-0 transition-colors duration-100 cursor-pointer flex items-start gap-2.5 ${
                  isSelected
                    ? 'bg-teal-50 border-l-[3px] border-l-teal-800'
                    : isAtTestLocation
                      ? 'bg-orange-50/30 border-l-[3px] border-l-orange-300 hover:bg-slate-50'
                      : 'border-l-[3px] border-l-transparent hover:bg-slate-50'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold truncate ${isSelected ? 'text-teal-800' : 'text-slate-800'}`}>
                      {cls.name}
                    </span>
                    <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
                      cls.status === 'Coming'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-emerald-100 text-emerald-700'
                    }`}>
                      {cls.status}
                    </span>
                    {isFull && (
                      <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-600">
                        {t('pipeline.full', 'Full')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    {cls.classDays?.length > 0 && (
                      <span className="flex items-center gap-1 text-[10px] text-slate-500">
                        <Clock size={9} className="flex-shrink-0" />
                        {formatSchedule(cls.classDays)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    {locName && (
                      <span className="flex items-center gap-1 text-[10px] text-slate-500">
                        <MapPin size={9} className="flex-shrink-0" />
                        {locName}
                      </span>
                    )}
                    <span className="flex items-center gap-1 text-[10px] text-slate-500">
                      <Users size={9} className="flex-shrink-0" />
                      {studentCount}{maxStudents > 0 ? `/${maxStudents}` : ''}
                    </span>
                  </div>
                </div>
                {isSelected && (
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-teal-800 flex items-center justify-center text-white mt-0.5">
                    <Check size={11} strokeWidth={3} />
                  </div>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
